/**
 * Admin → Traffic's data (worker/src/traffic.js, GET /api/dashboard/traffic): the snapshot's types and the pure views
 * the page draws from it, one site or the whole network at a time. Pure, so the tests pin them (tests/traffic.test.mts).
 *
 * The snapshot keeps each report as a part (ga.<report>, gsc.<site>.<report>), each with its last good data and, when the
 * latest pull failed, its error. GA4 rows carry `site` (a slug, 'other' for hosts outside the network, or 'network' for
 * the network-wide request: users and sessions aren't additive across sites, so those numbers come from GA4 itself).
 * Search Console is per site; the network view adds the sites up (clicks and impressions add; CTR is recomputed and the
 * position is weighted by impressions, as Search Console averages it).
 */

/** The site's own GA4 events (site/js/app.js, site/js/ward-alerts.js). The Worker marks these rows `custom` too. */
export const CUSTOM_EVENTS = ["address_lookup", "home_module_click", "ask_meeting_open", "your_member_change", "ward_alerts_signup",
  // docs/ANALYTICS.md: the key events first, then the rest
  "ward_alerts_confirm", "sign_up", "follow", "donate_click", "generate_lead", "login", "unfollow", "address_suggestion_pick", "share",
  "article_engaged", "video_start", "video_chapter", "transcript_search", "chart_download", "embed_copy", "race_view", "candidate_view", "page_not_found"] as const;
export const NETWORK = "network";

export interface Part<T> { at: string | null; data: T | null; error?: string; failedAt?: string }
export interface GaRow { site: string; [field: string]: string | number | boolean | undefined }
export interface GscRow { clicks: number; impressions: number; ctr: number; position: number; site?: string; query?: string; page?: string; date?: string; device?: string; country?: string }
export interface GscTotals { clicks: number; impressions: number; ctr: number; position: number }
export interface TrafficSite { slug: string; domain: string; name: string; host: string; searchConsole: string | null }
export interface TrafficSnapshot {
  version: number; generatedAt?: string; measurementId: string;
  ga?: { property: string | null; name: string | null };
  sites: TrafficSite[];
  ranges?: { ga: { current: { startDate: string; endDate: string }; prior: { startDate: string; endDate: string } }; gsc: { lagDays: number; current: { startDate: string; endDate: string }; prior: { startDate: string; endDate: string } } };
  parts: Record<string, Part<unknown>>;
  quota?: { ga?: { tokensPerDay?: { consumed?: number; remaining?: number } | null } };
  lastRun?: { at: string; by: string; ok: number; failed: number; failedParts?: string[]; error: string | null };
}
export interface GoogleConnection {
  configured: boolean; redirectUri: string; connected: boolean; status: "connected" | "revoked" | null;
  email: string | null; connectedAt: string | null; connectedBy: string | null; scopes: string[]; missingScopes: string[]; lastError: string | null;
  property: { id: string; name: string | null; stream: string | null } | null;
  searchConsole: { sites: Record<string, string | null>; unverified: string[] };
  discoveredAt: string | null; discoveryError: string | null;
}
export interface TrafficReport { connection: GoogleConnection; snapshot: TrafficSnapshot | null }

// ---------- formatting ----------

export type Kind = "count" | "rate" | "seconds" | "position";
export const count = (n: number) => Math.round(n).toLocaleString("en-US");
export const pct = (r: number) => `${(r * 100).toFixed(1)}%`;
export const duration = (s: number) => { const t = Math.round(s); return t < 60 ? `${t}s` : `${Math.floor(t / 60)}m ${String(t % 60).padStart(2, "0")}s`; };
export function format(v: number, kind: Kind): string {
  if (kind === "rate") return pct(v);
  if (kind === "seconds") return duration(v);
  if (kind === "position") return v ? v.toFixed(1) : "—";
  return count(v);
}

export interface Delta { text: string; dir: "up" | "down" | "flat"; good: boolean | null }
/**
 * The change against the prior 28 days: percent for amounts, points for rates ("+1.2 pts"). good says whether the move
 * is the good way (bounce rate and search position are better lower); null when flat or there's nothing to compare.
 */
export function delta(cur: number, prior: number | null | undefined, kind: Kind, lowerIsBetter = false): Delta | null {
  if (prior == null) return null;
  let n: number, text: string;
  if (kind === "rate") { n = (cur - prior) * 100; text = `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(1)} pts`; }
  else if (kind === "position") { n = cur - prior; text = `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(1)}`; }
  else if (!prior) return cur ? { text: "New", dir: "up", good: !lowerIsBetter } : null;
  else { n = ((cur - prior) / prior) * 100; text = `${n >= 0 ? "+" : "−"}${Math.abs(n) >= 10 ? Math.round(Math.abs(n)) : Math.abs(n).toFixed(1)}%`; }
  const flat = Math.abs(n) < (kind === "position" ? 0.05 : 0.5);
  const dir = flat ? "flat" : n > 0 ? "up" : "down";
  return { text: flat ? "No change" : text, dir, good: flat ? null : (dir === "up") !== lowerIsBetter };
}

// ---------- reading parts ----------

export const part = <T,>(s: TrafficSnapshot | null, name: string): Part<T> | null => (s?.parts?.[name] as Part<T>) ?? null;
const rowsOf = (s: TrafficSnapshot | null, name: string) => part<{ rows: GaRow[] }>(s, name)?.data?.rows ?? [];

/**
 * A GA4 part's rows for one view. A site: its own rows. The network: the part's network-wide rows when it has them;
 * parts that are per page (pages, landing, articles) have none, so every site's rows, each with its site.
 */
export function rowsFor(s: TrafficSnapshot | null, name: string, site: string): GaRow[] {
  const rows = rowsOf(s, name);
  if (site !== NETWORK) return rows.filter((r) => r.site === site);
  const net = rows.filter((r) => r.site === NETWORK);
  return net.length ? net : rows;
}

export const num = (r: GaRow | undefined, k: string) => Number(r?.[k] ?? 0) || 0;

// ---------- KPI tiles ----------

export const KPIS: { key: string; label: string; kind: Kind; lowerIsBetter?: boolean }[] = [
  { key: "activeUsers", label: "Users", kind: "count" },
  { key: "newUsers", label: "New users", kind: "count" },
  { key: "sessions", label: "Sessions", kind: "count" },
  { key: "screenPageViews", label: "Views", kind: "count" },
  { key: "engagementRate", label: "Engagement rate", kind: "rate" },
  { key: "engagementPerUser", label: "Engagement time per user", kind: "seconds" },
  { key: "averageSessionDuration", label: "Session duration", kind: "seconds" },
  { key: "bounceRate", label: "Bounce rate", kind: "rate", lowerIsBetter: true },
  { key: "keyEvents", label: "Key events", kind: "count" },
];
export interface Tile { key: string; label: string; value: string; raw: number; delta: Delta | null }

/** The KPI tiles for a view: this period's totals and their change (null before the first pull). */
export function kpis(s: TrafficSnapshot | null, site: string): Tile[] | null {
  const rows = rowsOf(s, "ga.totals").filter((r) => r.site === site);
  const cur = rows.find((r) => r.dateRange === "current"), prior = rows.find((r) => r.dateRange === "prior");
  if (!cur && !prior) return null;
  return KPIS.map((k) => ({ key: k.key, label: k.label, raw: num(cur, k.key), value: format(num(cur, k.key), k.kind),
    delta: delta(num(cur, k.key), prior ? num(prior, k.key) : null, k.kind, k.lowerIsBetter) }));
}

// ---------- trend ----------

const shortDay = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** One metric day by day for the last 28 days of the trend (it holds 56, the prior period first), with the prior
 *  period's total beside it. Points are labeled "Oct 3" for the chart's axis. */
export function trend(s: TrafficSnapshot | null, site: string, metric: string) {
  const byDay = new Map<string, number>();
  for (const r of rowsOf(s, "ga.trend")) if (r.site === site) byDay.set(String(r.date), (byDay.get(String(r.date)) ?? 0) + num(r, metric));
  const days = [...byDay.keys()].sort();
  const cur = days.slice(-28), prior = days.slice(0, -28).slice(-28);
  return {
    points: cur.map((d) => ({ t: shortDay(d), value: byDay.get(d)! })),
    total: cur.reduce((n, d) => n + byDay.get(d)!, 0),
    priorTotal: prior.length ? prior.reduce((n, d) => n + byDay.get(d)!, 0) : null,
    from: cur[0] ?? null, to: cur.at(-1) ?? null,
  };
}

// ---------- events ----------

/** The site's own events first (zero when GA4 saw none), then the busiest of the rest. */
export function events(s: TrafficSnapshot | null, site: string, top = 15) {
  const rows = rowsFor(s, "ga.events", site);
  const by = new Map(rows.map((r) => [String(r.eventName), r]));
  return {
    custom: CUSTOM_EVENTS.map((name) => ({ name, count: num(by.get(name), "eventCount"), users: num(by.get(name), "activeUsers") })),
    other: rows.filter((r) => !CUSTOM_EVENTS.includes(String(r.eventName) as never)).slice(0, top)
      .map((r) => ({ name: String(r.eventName), count: num(r, "eventCount"), users: num(r, "activeUsers") })),
  };
}

/** home_module_click by module, or null while the parameter isn't a registered custom dimension. */
export function moduleClicks(s: TrafficSnapshot | null, site: string) {
  const d = part<{ registered: boolean; dimension: string; rows: GaRow[] }>(s, "ga.moduleClicks")?.data;
  if (!d?.registered) return null;
  const rows = site === NETWORK ? d.rows.filter((r) => r.site === NETWORK) : d.rows.filter((r) => r.site === site);
  return rows.map((r) => ({ module: String(r[d.dimension] ?? "(not set)"), count: num(r, "eventCount") }));
}

// ---------- Search Console ----------

/** Search Console totals added across sites: clicks and impressions add; CTR is clicks / impressions; position is
 *  weighted by impressions. */
export function addGsc(list: GscTotals[]): GscTotals {
  const clicks = list.reduce((n, t) => n + t.clicks, 0), impressions = list.reduce((n, t) => n + t.impressions, 0);
  return { clicks, impressions, ctr: impressions ? clicks / impressions : 0, position: impressions ? list.reduce((n, t) => n + t.position * t.impressions, 0) / impressions : 0 };
}

export interface GscView {
  sites: TrafficSite[];                     // the verified sites this view covers
  unverified: TrafficSite[];                // the sites in it Search Console can't read
  totals: { current: GscTotals; prior: GscTotals } | null;
  trend: { t: string; value: number }[];    // clicks per day, the last 28 settled days
  queries: GscRow[]; pages: GscRow[]; striking: GscRow[]; devices: GscRow[]; countries: GscRow[];
  errors: { part: string; error: string }[];
}

/** The Search Console view for one site or the network (each row tagged with its site; lists cut to `top`). */
export function gscView(s: TrafficSnapshot | null, site: string, top = 50): GscView {
  const all = s?.sites ?? [];
  const covered = site === NETWORK ? all : all.filter((x) => x.slug === site);
  const sites = covered.filter((x) => x.searchConsole), unverified = covered.filter((x) => !x.searchConsole);
  const get = <T,>(slug: string, name: string) => part<T>(s, `gsc.${slug}.${name}`);
  const rows = (name: string, sortBy: "clicks" | "impressions") => sites
    .flatMap((x) => (get<{ rows: GscRow[] }>(x.slug, name)?.data?.rows ?? []).map((r) => ({ ...r, site: x.slug })))
    .sort((a, b) => b[sortBy] - a[sortBy]);
  const totals = sites.map((x) => get<{ current: GscTotals; prior: GscTotals }>(x.slug, "totals")?.data).filter(Boolean) as { current: GscTotals; prior: GscTotals }[];
  const byDay = new Map<string, number>();
  for (const r of rows("trend", "clicks")) byDay.set(String(r.date), (byDay.get(String(r.date)) ?? 0) + r.clicks);
  const cut = s?.ranges?.gsc.current.startDate ?? "";
  const merge = (name: string, key: "device" | "country") => {
    const m = new Map<string, GscTotals[]>();
    for (const r of rows(name, "clicks")) m.set(String(r[key]), [...(m.get(String(r[key])) ?? []), r]);
    return [...m].map(([k, list]) => ({ [key]: k, ...addGsc(list) }) as GscRow).sort((a, b) => b.clicks - a.clicks);
  };
  return {
    sites, unverified,
    totals: totals.length ? { current: addGsc(totals.map((t) => t.current)), prior: addGsc(totals.map((t) => t.prior)) } : null,
    trend: [...byDay.keys()].sort().filter((d) => d >= cut).map((d) => ({ t: shortDay(d), value: byDay.get(d)! })),
    queries: rows("queries", "clicks").slice(0, top), pages: rows("pages", "clicks").slice(0, top),
    striking: rows("striking", "impressions").slice(0, top),
    devices: merge("devices", "device"), countries: merge("countries", "country").slice(0, 15),
    errors: sites.flatMap((x) => Object.keys(s?.parts ?? {}).filter((k) => k.startsWith(`gsc.${x.slug}.`) && s!.parts[k].error).map((k) => ({ part: k, error: s!.parts[k].error! }))),
  };
}

/** The GA4 parts whose latest pull failed (they still show their last good data, from `at`). */
export const gaErrors = (s: TrafficSnapshot | null) =>
  Object.entries(s?.parts ?? {}).filter(([k, p]) => k.startsWith("ga.") && p.error).map(([k, p]) => ({ part: k, error: p.error!, at: p.at }));

/** A page path as a link on its site ("/ward/3" on www.nycpolitics.org); a full URL (Search Console) as it is. */
export function pageUrl(s: TrafficSnapshot | null, site: string, path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const host = s?.sites.find((x) => x.slug === site)?.host;
  return host ? `https://${host}${path}` : path;
}
/** "nyc" → "NYC Politics" (the site's name), 'other' → "Other hosts". */
export const siteName = (s: TrafficSnapshot | null, slug: string) => (slug === "other" ? "Other hosts" : s?.sites.find((x) => x.slug === slug)?.name ?? slug);
